/* PHE 2.0 — Boutique behaviours.
 * Everything is event-delegated from document so it keeps working after
 * Horizon morphs/re-renders sections (variant change, section rendering API).
 */
(() => {
  if (window.__pheBoutique) return;
  window.__pheBoutique = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Announcement rotation ---------- */
  function initAnnouncements(root = document) {
    root.querySelectorAll('[data-phe-announce]:not([data-ready])').forEach((el) => {
      el.dataset.ready = '1';
      const msgs = [...el.querySelectorAll('.phe-announce__msg')];
      if (msgs.length < 2 || reduceMotion) return;
      const ms = (parseInt(el.dataset.seconds, 10) || 5) * 1000;
      let i = 0;
      let timer = setInterval(next, ms);
      function next() {
        msgs[i].classList.remove('is-active');
        msgs[i].setAttribute('aria-hidden', 'true');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
        msgs[i].removeAttribute('aria-hidden');
      }
      el.addEventListener('mouseenter', () => clearInterval(timer));
      el.addEventListener('mouseleave', () => { clearInterval(timer); timer = setInterval(next, ms); });
      el.addEventListener('focusin', () => clearInterval(timer));
    });
  }

  /* ---------- Tabs (bestsellers) ---------- */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-phe-tab]');
    if (!tab) return;
    const root = tab.closest('[data-phe-tabs]');
    root.querySelectorAll('[data-phe-tab]').forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    root.querySelectorAll('[data-phe-panel]').forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
  });
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest('[data-phe-tab]');
    if (!tab || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    const tabs = [...tab.closest('[data-phe-tabs]').querySelectorAll('[data-phe-tab]')];
    const idx = (tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[idx].focus();
    tabs[idx].click();
  });

  /* ---------- Selling plan picker ----------
   * Radios carry form="phe-detached" so they are never serialised into the
   * product form. Only the <select name="selling_plan"> is submitted, and it
   * is disabled (therefore excluded from FormData) for one-time purchase.
   */
  function syncPlan(root) {
    const select = root.querySelector('select[name="selling_plan"]');
    const chosen = root.querySelector('input[data-phe-purchase]:checked');
    if (!select || !chosen) return;
    const subscribe = chosen.value === 'subscribe';
    select.disabled = !subscribe;
    if (!subscribe) {
      select.dataset.last = select.value || select.dataset.last || '';
      select.value = '';
    } else if (!select.value) {
      select.value = select.dataset.last || select.dataset.default || (select.options[0] && select.options[0].value) || '';
    }
    const opt = select.selectedOptions[0];
    const price = root.querySelector('[data-phe-plan-price]');
    if (price && opt && opt.dataset.price) price.textContent = opt.dataset.price;
    root.dataset.mode = subscribe ? 'subscribe' : 'onetime';
  }
  function initPlans(scope = document) {
    scope.querySelectorAll('[data-phe-plan]').forEach(syncPlan);
  }
  document.addEventListener('change', (e) => {
    const root = e.target.closest('[data-phe-plan]');
    if (root) syncPlan(root);
  });

  /* ---------- Size / fit drawer ---------- */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-phe-drawer-open]');
    if (open) {
      const d = document.getElementById(open.getAttribute('aria-controls'));
      if (d && d.showModal) { d.showModal(); e.preventDefault(); }
      return;
    }
    const close = e.target.closest('[data-phe-drawer-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    if (e.target.matches('dialog.phe-drawer')) e.target.close();
  });

  /* ---------- Add to bag: cart/add.js, then refresh Horizon's drawer in place ---------- */
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function toast(msg) {
    let t = document.querySelector('.phe-toast');
    if (!t) { t = document.createElement('div'); t.className = 'phe-toast'; t.setAttribute('role', 'status'); document.body.appendChild(t); }
    t.textContent = msg; t.classList.add('is-on');
    clearTimeout(t._h); t._h = setTimeout(() => t.classList.remove('is-on'), 2600);
  }
  async function refreshCartUI() {
    const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const icon = document.querySelector('cart-icon');
    if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count);
    const drawer = document.getElementById('cart-drawer');
    const current = drawer && drawer.querySelector('.cart-drawer__inner');
    if (!drawer || !current) return { cart, opened: false };
    const res = await fetch(`${root()}?sections=cart-drawer-section`);
    const html = (await res.json())['cart-drawer-section'];
    const fresh = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer__inner');
    if (!fresh) return { cart, opened: false };
    current.replaceWith(document.importNode(fresh, true));
    if (typeof drawer.open === 'function') { drawer.open(); return { cart, opened: true }; }
    return { cart, opened: false };
  }
  async function addToBag(items) {
    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error((await res.json()).description || 'Could not add to bag');
    let opened = false;
    try { ({ opened } = await refreshCartUI()); } catch (_) { /* fall through */ }
    updateShipBars();
    return opened;
  }
  window.PHE = Object.assign(window.PHE || {}, { addToBag });

  /* Wardrobe builder */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-phe-wardrobe]');
    if (!form) return;
    e.preventDefault();
    const ids = [...form.querySelectorAll('input[name="phe_piece"]:checked')].map((i) => ({ id: Number(i.value), quantity: 1 }));
    const status = form.querySelector('[data-phe-status]');
    const btn = form.querySelector('button[type="submit"]');
    if (!ids.length) { if (status) status.textContent = form.dataset.emptyMsg || 'Select at least one piece.'; return; }
    btn.disabled = true;
    try {
      const opened = await addToBag(ids);
      if (!opened) { window.location.href = `${root()}cart`; return; }
      if (status) status.textContent = '';
    } catch (err) {
      if (status) status.textContent = err.message;
    }
    btn.disabled = false;
  });

  /* Card quick add (single-variant products only) */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-phe-add]');
    if (!btn) return;
    e.preventDefault();
    btn.setAttribute('aria-busy', 'true');
    try {
      const opened = await addToBag([{ id: Number(btn.dataset.pheAdd), quantity: 1 }]);
      if (!opened) toast(btn.dataset.added || 'Added to bag');
    } catch (err) {
      toast(err.message);
    }
    btn.removeAttribute('aria-busy');
  });

  /* ---------- Free AU shipping progress (cart drawer + cart page) ---------- */
  const SHIP_TARGETS = '#cart-drawer .cart-drawer__summary, .cart-page .cart__summary-totals, main .cart__summary-totals';
  let shipTimer;
  function money(cents) {
    const fmt = (window.PHE && window.PHE.moneyFormat) || '${{amount}}';
    return formatMoney(cents, fmt);
  }
  async function updateShipBars() {
    const cfg = window.PHE || {};
    if (!cfg.freeShip || cfg.country !== 'AU') return;
    const targets = document.querySelectorAll(SHIP_TARGETS);
    if (!targets.length) return;
    let cart;
    try { cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json(); } catch (_) { return; }
    const total = cart.total_price;
    const left = Math.max(cfg.freeShip - total, 0);
    const pct = Math.min(100, Math.round((total / cfg.freeShip) * 100));
    const text = left > 0
      ? `You're ${money(left)} away from free standard shipping on eligible orders`
      : 'Free standard shipping unlocked on eligible orders';
    targets.forEach((t) => {
      let bar = t.querySelector(':scope > .phe-shipbar');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'phe-shipbar';
        bar.setAttribute('role', 'status');
        bar.innerHTML = '<span class="phe-shipbar__text"></span><div class="phe-shipbar__track" aria-hidden="true"><div class="phe-shipbar__fill"></div></div>';
        t.prepend(bar);
      }
      bar.classList.toggle('phe-shipbar--done', left === 0);
      bar.style.setProperty('--phe-progress', `${pct}%`);
      const tx = bar.querySelector('.phe-shipbar__text');
      if (tx.textContent !== text) tx.textContent = text;
    });
  }
  function queueShip() { clearTimeout(shipTimer); shipTimer = setTimeout(updateShipBars, 250); }

  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-phe-wardrobe]');
    if (!form || e.target.name !== 'phe_piece') return;
    const fmt = form.dataset.moneyFormat;
    let cents = 0;
    form.querySelectorAll('input[name="phe_piece"]:checked').forEach((i) => { cents += Number(i.dataset.price || 0); });
    const out = form.querySelector('[data-phe-total]');
    if (out) out.textContent = formatMoney(cents, fmt);
  });
  function formatMoney(cents, format = '${{amount}}') {
    const v = (cents / 100).toFixed(2);
    const [w, d] = v.split('.');
    const withCommas = w.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${w.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${d}`)
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${d}`);
  }

  /* ---------- Boot + re-init after Horizon re-renders ---------- */
  function boot(scope) { initAnnouncements(scope); initPlans(scope); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  new MutationObserver((muts) => {
    let reboot = false;
    let cartChanged = false;
    for (const m of muts) {
      if (m.target.closest && m.target.closest('.phe-shipbar')) continue;
      for (const n of m.addedNodes) {
        if (n.nodeType !== 1 || n.classList.contains('phe-shipbar')) continue;
        if (n.matches('[data-phe-plan],[data-phe-announce]') || n.querySelector('[data-phe-plan],[data-phe-announce]')) reboot = true;
        if (n.closest('#cart-drawer, .cart-page, cart-items-component') || n.querySelector('.cart-drawer__summary, .cart__summary-totals')) cartChanged = true;
      }
    }
    if (reboot) boot(document);
    if (cartChanged) queueShip();
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queueShip); else queueShip();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
